import { useContext, useState, useMemo, useCallback, Fragment } from "react";
import { AlquileresContext } from "../../../../context/AlquileresContext";
import { ActividadContext } from "../../../../context/ActividadContext";
import { saveLocador, updateLocador, deleteLocador } from "../../../../application/locadores";
import { FaPlusCircle, FaEdit, FaTrash, FaSearch, FaTimes, FaChevronDown } from "react-icons/fa";
import { useSortableData } from "../../../../hooks/useSortableData";
import SortableHeader from "../../../../components/SortableHeader";
import { nombreCompleto } from "../../../../utils/nombre";
import { formatMonto, formatFechaCorta } from "../../../../utils/rentalCalculations";

function LocadorList() {
  const { locadores, locatarios, contratos, refreshLocadores, refreshContratos } = useContext(AlquileresContext);
  const { registrarActividad } = useContext(ActividadContext);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null); // null | { mode: 'create' | 'edit', data }
  const [form, setForm] = useState({ apellido: "", nombre: "", telefono: "", telefono2: "", email: "", observaciones: "" });
  const [saving, setSaving] = useState(false);
  const [expandidos, setExpandidos] = useState(() => new Set());

  // Contratos por locador (con el nombre del locatario resuelto por id).
  const contratosPorLocador = useMemo(() => {
    const locatarioPorId = {};
    locatarios.forEach((p) => { if (p.idFirebase) locatarioPorId[p.idFirebase] = nombreCompleto(p); });
    const map = {};
    contratos.forEach((c) => {
      if (!c.locadorId) return;
      const item = {
        ...c,
        locatarioNombre: c.locatarioNombre || locatarioPorId[c.locatarioId] || "",
      };
      (map[c.locadorId] = map[c.locadorId] || []).push(item);
    });
    return map;
  }, [contratos, locatarios]);

  const toggleExpandido = useCallback((id) => {
    setExpandidos((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  // Orden de la sub-tabla de contratos (compartido por todos los desplegados).
  const [subSort, setSubSort] = useState({ key: "propiedad", direction: "asc" });
  const requestSubSort = useCallback((key) => {
    setSubSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  }, []);
  const ordenarContratos = useCallback(
    (items) => {
      const valor = (c) => {
        switch (subSort.key) {
          case "monto": return Number(c.montoActual) || 0;
          case "vence": return c.fechaVencimiento || "";
          case "estado": return c.estado || "";
          case "locatario": return c.locatarioNombre || "";
          default: return c.propiedad || "";
        }
      };
      const copy = [...items];
      copy.sort((a, b) => {
        const va = valor(a);
        const vb = valor(b);
        const cmp =
          typeof va === "number" && typeof vb === "number"
            ? va - vb
            : String(va).localeCompare(String(vb), "es", { sensitivity: "base", numeric: true });
        return subSort.direction === "asc" ? cmp : -cmp;
      });
      return copy;
    },
    [subSort]
  );

  const filtered = useMemo(() => {
    return locadores.filter(
      (l) =>
        !search ||
        nombreCompleto(l).toLowerCase().includes(search.toLowerCase()) ||
        l.telefono?.includes(search) ||
        l.telefono2?.includes(search) ||
        l.observaciones?.toLowerCase().includes(search.toLowerCase())
    );
  }, [locadores, search]);

  const contratosCount = useCallback(
    (locadorId) => (contratosPorLocador[locadorId] || []).length,
    [contratosPorLocador]
  );

  const getValue = useCallback(
    (l, key) => {
      if (key === "contratos") return contratosCount(l.idFirebase);
      if (key === "nombre") return nombreCompleto(l);
      return l?.[key];
    },
    [contratosCount]
  );
  const { sortedData, sortConfig, requestSort } = useSortableData(filtered, {
    getValue,
    storageKey: "sort-locadores",
    initial: { key: "nombre", direction: "asc" },
  });

  const openCreate = () => {
    setForm({ apellido: "", nombre: "", telefono: "", telefono2: "", email: "", observaciones: "" });
    setModal({ mode: "create" });
  };

  const openEdit = (loc) => {
    setForm({
      apellido: loc.apellido || "",
      nombre: loc.nombre || "",
      telefono: loc.telefono || "",
      telefono2: loc.telefono2 || "",
      email: loc.email || "",
      observaciones: loc.observaciones || "",
    });
    setModal({ mode: "edit", data: loc });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.apellido.trim()) return;
    setSaving(true);
    try {
      if (modal.mode === "create") {
        await saveLocador(form);
        registrarActividad("LOCADOR_CREADO", {
          mensaje: `Locador creado: ${nombreCompleto(form)}`,
          entidadTipo: "LOCADOR",
          entidadLabel: nombreCompleto(form),
        });
      } else {
        await updateLocador(modal.data.idFirebase, form);
        // the rename cascades onto contratos, so refresh them too
        await refreshContratos();
      }
      await refreshLocadores();
      setModal(null);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este locador?")) return;
    const locador = locadores.find((l) => l.idFirebase === id);
    try {
      await deleteLocador(id);
      await refreshLocadores();
      if (locador) {
        registrarActividad("LOCADOR_ELIMINADO", {
          mensaje: `Locador eliminado: ${nombreCompleto(locador)}`,
          entidadTipo: "LOCADOR",
          entidadLabel: nombreCompleto(locador),
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <div className="px-4 md:px-6 py-4 space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Locadores</h1>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          <FaPlusCircle className="w-4 h-4" /> Nuevo Locador
        </button>
      </div>

      <div className="relative max-w-md">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder="Buscar por nombre, telefono u observaciones..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <SortableHeader sortKey="nombre" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Apellido y Nombre</SortableHeader>
              <SortableHeader sortKey="telefono" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Telefono</SortableHeader>
              <SortableHeader sortKey="telefono2" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Telefono 2</SortableHeader>
              <SortableHeader sortKey="email" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Email</SortableHeader>
              <SortableHeader sortKey="observaciones" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Observaciones</SortableHeader>
              <SortableHeader sortKey="contratos" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="center" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Contratos</SortableHeader>
              <th className="text-center px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  No se encontraron locadores
                </td>
              </tr>
            ) : (
              sortedData.map((l) => {
                const cant = contratosCount(l.idFirebase);
                const abierto = expandidos.has(l.idFirebase);
                const items = contratosPorLocador[l.idFirebase] || [];
                return (
                <Fragment key={l.idFirebase}>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-200">{nombreCompleto(l)}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">{l.telefono || "-"}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">{l.telefono2 || "-"}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{l.email || "-"}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-xs truncate" title={l.observaciones || ""}>
                    {l.observaciones || "-"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {cant > 0 ? (
                      <button
                        onClick={() => toggleExpandido(l.idFirebase)}
                        className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full transition-colors ${
                          abierto
                            ? "bg-primary text-white"
                            : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                        }`}
                        title={abierto ? "Ocultar contratos" : "Ver contratos"}
                      >
                        {cant}
                        <FaChevronDown className={`w-2.5 h-2.5 transition-transform ${abierto ? "rotate-180" : ""}`} />
                      </button>
                    ) : (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-400">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => openEdit(l)} className="text-gray-400 hover:text-primary transition-colors">
                        <FaEdit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(l.idFirebase)} className="text-gray-400 hover:text-red-500 transition-colors">
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                {abierto && (
                  <tr className="bg-gray-50 dark:bg-gray-900/30">
                    <td colSpan={7} className="px-4 pb-3 pt-0">
                      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                              <SortableHeader sortKey="propiedad" currentKey={subSort.key} direction={subSort.direction} onSort={requestSubSort} align="left" className="px-3 py-1.5 font-semibold">Propiedad</SortableHeader>
                              <SortableHeader sortKey="locatario" currentKey={subSort.key} direction={subSort.direction} onSort={requestSubSort} align="left" className="px-3 py-1.5 font-semibold">Locatario</SortableHeader>
                              <SortableHeader sortKey="monto" currentKey={subSort.key} direction={subSort.direction} onSort={requestSubSort} align="right" className="px-3 py-1.5 font-semibold">Monto</SortableHeader>
                              <SortableHeader sortKey="estado" currentKey={subSort.key} direction={subSort.direction} onSort={requestSubSort} align="center" className="px-3 py-1.5 font-semibold">Estado</SortableHeader>
                              <SortableHeader sortKey="vence" currentKey={subSort.key} direction={subSort.direction} onSort={requestSubSort} align="center" className="px-3 py-1.5 font-semibold">Vence</SortableHeader>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {ordenarContratos(items).map((c) => (
                              <tr key={c.idFirebase} className="bg-white dark:bg-gray-800/40">
                                <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{c.propiedad || "-"}</td>
                                <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{c.locatarioNombre || "-"}</td>
                                <td className="px-3 py-1.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatMonto(c.montoActual, c.moneda)}</td>
                                <td className="px-3 py-1.5 text-center text-gray-500 dark:text-gray-400">{c.estado || "-"}</td>
                                <td className="px-3 py-1.5 text-center text-gray-500 dark:text-gray-400 whitespace-nowrap">{c.fechaVencimiento ? formatFechaCorta(c.fechaVencimiento) : "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-xl mx-4 p-8">
            <div className="flex items-center justify-between pb-4 mb-5 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                {modal.mode === "create" ? "Nuevo Locador" : "Editar Locador"}
              </h3>
              <button onClick={() => setModal(null)} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre *</label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Apellido *</label>
                  <input
                    type="text"
                    value={form.apellido}
                    onChange={(e) => setForm({ ...form, apellido: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefono</label>
                  <input
                    type="text"
                    value={form.telefono}
                    onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefono 2</label>
                  <input
                    type="text"
                    value={form.telefono2}
                    onChange={(e) => setForm({ ...form, telefono2: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Observaciones</label>
                <textarea
                  value={form.observaciones}
                  onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                  rows={2}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  placeholder="Notas internas, alias, etc."
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-gray-100 dark:border-gray-700">
                <button type="button" onClick={() => setModal(null)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                  Cancelar
                </button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-primary hover:bg-secondary text-white rounded-lg disabled:opacity-50">
                  {saving ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default LocadorList;
